import * as types from '@/store/mutation-types';
import api from '@/store/api';
import { logAction } from '../logActions';
import log from '@/logger';
import router from '../../router';
import * as filters from '@/common/filters';
import moment from 'moment';

export const getDefaultDiaryEntry = async ({ commit, state }, values) => {
  console.debug('DiaryEntryActions:getDefaultDiaryEntry');
  if (!values) {
    values = {};
  }
  return api.getDefaultDiaryEntry(values.projectId);
};

export const loadProjectDiaryEntries = async ({ dispatch, commit }, { projectId }) => {
  let list = await dispatch('getDiaryEntries', { projectId });
  commit(types.setDiaryEntries, list);
  return list;
};

export const getDiaryEntry = async ({ commit, state }, id) => {
  console.debug('DiaryEntryActions:getDiaryEntry');
  return api.getDiaryEntry(id);
};

export const getPreviousDiaryEntryForDate = async ({ dispatch, getters, state }, date) => {
  console.debug('DiaryEntryActions:getPreviousDiaryEntryForDate');

  let previousDate = '1900-01-01';
  let found;
  for (let diaryEntry of state.currentProjectDiaryEntries) {
    if (
      !diaryEntry.archived &&
      diaryEntry.signedOff &&
      !diaryEntry.noWorkPerformed &&
      moment(diaryEntry.diaryDate).isBefore(date) &&
      moment(diaryEntry.diaryDate).isAfter(previousDate)
    ) {
      found = diaryEntry;
      previousDate = found.diaryDate;
    }
  }
  if (found) {
    return found;
  }
  return undefined;
};

export const getDiaryEntryForDate = async ({ dispatch, getters, state }, date) => {
  console.debug('DiaryEntryActions:getDiaryEntryForDate');

  let diaryEntry = state.currentProjectDiaryEntries.find((i) => i.diaryDate === date);
  if (diaryEntry && diaryEntry.unassigned.length === 0) {
    return diaryEntry;
  }

  if (!diaryEntry) {
    diaryEntry = await api.getDefaultDiaryEntry(state.currentProject._id);
  }
  let parts = filters.filterParts(state, state.currentProject, '', date, date, { excludeLockedDiaries: true });
  parts = filters.flattenParts(parts);

  let collected = parts.reduce(
    (collected, part) => {
      let cmpId;
      if (part.part.partSubcontractorId) {
        let person = getters.getPerson(part.part.partSubcontractorId);
        cmpId = person.companyId;
      }
      if (!cmpId && part.occupation) {
        let userItem = getters.currentProjectUserItem(part.occupation);
        cmpId = userItem ? userItem.companyId : undefined;
      }

      if (cmpId) {
        collected.companies.add(cmpId);
      } else {
        collected.unassigned.add(part.part._id);
      }
      return collected;
    },
    { companies: new Set(), unassigned: new Set() }
  );

  let performingCompanies = [];
  for (let item of collected.companies) {
    performingCompanies.push({
      companyId: item,
      diaryEntries: [api.getDefaultDiaryEntryItem()],
    });
  }

  return dispatch('saveDiaryEntryProperties', {
    diaryEntry,
    currentUserId: state.loggedInPerson._id,
    diaryDate: date,
    unassigned: Array.from(collected.unassigned),
    performingCompanies,
  });
};

export const mergeDiaryEntry = async ({ getters, state, dispatch }, update) => {
  log.debug('DiaryEntryActions:mergeDiaryEntry', update);

  let destEntries = Object.assign([], update.destinationDiaryEntry.performingCompanies);

  for (let entry of update.sourceDiaryEntry.performingCompanies) {
    let oldEntry = destEntries.find((i) => i.companyId === entry.companyId);
    if (oldEntry) {
      destEntries = destEntries.filter((i) => i.companyId !== entry.companyId);
      destEntries.push(entry);
    }
  }

  return dispatch('saveDiaryEntryProperties', {
    diaryEntry: update.destinationDiaryEntry,
    performingCompanies: destEntries,
    summary: update.sourceDiaryEntry.summary,
  });
};

export const updateDiaryUnassigned = async ({ getters, state, dispatch }, update) => {
  log.debug('DiaryEntryActions:updateDiaryUnassigned', update);

  let userItem = getters.currentProjectUserItem(update.part.partOccupation);
  if (userItem && !userItem.companyId && !userItem.personId) {
    let person = state.persons[update.part.partSubcontractorId];

    await dispatch('updateProjectUserItem', {
      project: state.currentProject,
      userItem,
      property: 'companyId',
      newValue: person.companyId,
    });
    await dispatch('updateProjectUserItem', {
      project: state.currentProject,
      userItem,
      property: 'personId',
      newValue: person._id,
    });
  }

  let performingCompanies = Object.assign([], update.diaryEntry.performingCompanies);
  let person = state.persons[update.part.partSubcontractorId];
  userItem = getters.currentProjectUserItem(update.part.partOccupation);
  let addCompanyId = person ? person.companyId : userItem ? userItem.companyId : undefined;

  if (!addCompanyId) {
    return;
  }

  if (addCompanyId && !update.diaryEntry.performingCompanies.find((i) => i.companyId === addCompanyId)) {
    performingCompanies.push({
      companyId: addCompanyId,
      diaryEntries: [api.getDefaultDiaryEntryItem()],
    });
  }

  // Remove parts from unassigend list, where occupation matches set occupation
  let unassigned = update.diaryEntry.unassigned.filter((i) => {
    let unassignedPart = state.parts[i];
    return (
      (unassignedPart.partOccupation === '' && unassignedPart._id !== update.part._id) ||
      unassignedPart.partOccupation !== update.part.partOccupation
    );
  });

  return dispatch('saveDiaryEntryProperties', { diaryEntry: update.diaryEntry, unassigned, performingCompanies });
};

export const saveDiaryEntryProperties = ({ commit, state, dispatch }, update) => {
  log.debug('DiaryEntryActions:saveDiaryEntryProperties', JSON.stringify(update, '', '  '));
  // console.log(JSON.stringify(update));
  var newProperties = Object.assign({}, update);
  delete newProperties.diaryEntry;

  var orig = Object.assign({}, update.diaryEntry);
  var updated = Object.assign({}, orig, newProperties);

  if (orig._id && JSON.stringify(orig) === JSON.stringify(updated)) {
    return orig;
  }

  logAction(
    { commit, state },
    { object: update.diaryEntry, reason: 'Updated DiaryEntry Properties', update: newProperties }
  );
  setDiaryEntryProperties({ commit, state }, update);

  return api
    .saveDiaryEntry(update.diaryEntry)
    .then(async (revInfo) => {
      commit(types.setDiaryEntryProperties, { diaryEntry: update.diaryEntry, newProperties: revInfo });
      commit(types.addDiaryEntry, { newDiaryEntry: update.diaryEntry });

      return update.diaryEntry;
    })
    .catch((e) => {
      log.error('Error while saving DiaryEntry properties', e);
      commit(types.setSyncStatus, {
        status: 'Error',
        live: 0,
        message: 'Database Error occurred while saving DiaryEntry properties',
        count: 1,
      });
      throw e;
    });
};

export const setDiaryEntryProperties = ({ commit, state }, update) => {
  log.debug('DiaryEntryActions:setDiaryEntryProperties', update);

  var newProperties = Object.assign({}, update);
  delete newProperties.diaryEntry;

  commit(types.setDiaryEntryProperties, { diaryEntry: update.diaryEntry, newProperties });
};

export const getDiaryEntries = ({ commit, state }, { projectId }) => {
  log.debug('DiaryEntryActions:getDiaryEntries', projectId);
  return api.getDiaryEntries(projectId);
};

export const syncDiaryEntry = ({ commit, state }, diaryEntry) => {
  log.debug('DiaryEntryActions:syncDiaryEntry', diaryEntry);
  if (diaryEntry.projectId === state.currentProject._id) {
    let foundItem = state.currentProjectDiaryEntries.find((i) => {
      return i._id === diaryEntry._id;
    });

    if (foundItem) {
      if (foundItem._rev !== diaryEntry._rev) {
        commit(types.setDiaryEntryProperties, { diaryEntry: foundItem, newProperties: diaryEntry });
      }
    } else {
      commit(types.addDiaryEntry, { newDiaryEntry: diaryEntry });
    }
  }
};

export const selectDiaryEntry = ({ commit, state }, date) => {
  log.debug('actions:selectDiaryEntry', date);

  let diaryEntry;
  if (date) {
    let d = state.currentProjectDiaryEntries.filter((i) => i.diaryDate === date);
    if (d.length > 0) {
      diaryEntry = d[0];
    }
  }

  if (diaryEntry) {
    if (router.currentRoute.params._id !== diaryEntry.projectId || router.currentRoute.params.date !== date) {
      router.push({
        name: 'ProjectPlanDiaryEntry',
        params: { _id: diaryEntry.projectId, date: date },
        query: router.currentRoute.query,
      });
    }

    commit(types.selectPart, null);
    commit(types.selectStage, null);
    commit(types.selectDiaryEntry, diaryEntry || null);
  } else {
    commit(types.selectDiaryEntry, null);
  }
};

export const diaryEntryRemoveItem = ({ commit }, { diaryEntry, entry, index }) => {
  log.debug('DiaryEntryActions:diaryEntryRemoveItem', { diaryEntry, entry, index });
  commit(types.diaryEntryRemoveItem, { entry, index });
  return api
    .saveDiaryEntry(diaryEntry)
    .then(async (revInfo) => {
      commit(types.setDiaryEntryProperties, { diaryEntry: diaryEntry, newProperties: revInfo });

      return diaryEntry;
    })
    .catch((e) => {
      log.error('Error while removing DiaryEntry entry', e);
      commit(types.setSyncStatus, {
        status: 'Error',
        live: 0,
        message: 'Database Error occurred while removing DiaryEntry entry',
        count: 1,
      });
      throw e;
    });
};

export const diaryEntryAddItem = ({ commit }, { diaryEntry, entry }) => {
  commit(types.diaryEntryAddItem, { entry, newItem: api.getDefaultDiaryEntryItem() });
  return api
    .saveDiaryEntry(diaryEntry)
    .then(async (revInfo) => {
      commit(types.setDiaryEntryProperties, { diaryEntry: diaryEntry, newProperties: revInfo });

      return diaryEntry;
    })
    .catch((e) => {
      log.error('Error while adding DiaryEntry entry', e);
      commit(types.setSyncStatus, {
        status: 'Error',
        live: 0,
        message: 'Database Error occurred while adding DiaryEntry entry',
        count: 1,
      });
      throw e;
    });
};

export const diaryEntrySetItemProperties = ({ commit, state }, update) => {
  log.debug('DiaryEntryActions:diaryEntrySetItemProperties', update);

  var newProperties = Object.assign({}, update);
  delete newProperties.diaryEntry;
  delete newProperties.diaryEntryItem;

  commit(types.diaryEntrySetItemProperties, { diaryEntryItem: update.diaryEntryItem, newProperties });

  return api
    .saveDiaryEntry(update.diaryEntry)
    .then(async (revInfo) => {
      commit(types.setDiaryEntryProperties, { diaryEntry: update.diaryEntry, newProperties: revInfo });

      return update.diaryEntry;
    })
    .catch((e) => {
      log.error('Error while adding DiaryEntry entry', e);
      commit(types.setSyncStatus, {
        status: 'Error',
        live: 0,
        message: 'Database Error occurred while adding DiaryEntry entry',
        count: 1,
      });
      throw e;
    });
};



// WEBPACK FOOTER //
// ./src/store/diaryEntry/diaryEntryActions.js